import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';

const ThreeModelViewer = ({ url, width = 60, height = 60, interactive = false, autoRotate = true }) => {
    const mountRef = useRef(null);

    useEffect(() => {
        if (!mountRef.current || !url) return;

        const container = mountRef.current;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf5f5f5);

        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 10000);

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        container.appendChild(renderer.domElement);

        // Lighting
        scene.add(new THREE.AmbientLight(0xffffff, 0.6));
        const dirLight = new THREE.DirectionalLight(0xffffff, 0.8);
        dirLight.position.set(5, 10, 7);
        scene.add(dirLight);
        const backLight = new THREE.DirectionalLight(0xffffff, 0.3);
        backLight.position.set(-5, -3, -5);
        scene.add(backLight);

        // Controls (only for interactive/modal mode)
        let controls;
        if (interactive) {
            controls = new OrbitControls(camera, renderer.domElement);
            controls.enableDamping = true;
            controls.autoRotate = autoRotate;
            controls.autoRotateSpeed = 2;
        }

        // Pick loader based on extension
        const ext = url.split('.').pop().split('?')[0].toLowerCase();
        const loader = ext === 'fbx' ? new FBXLoader() : new OBJLoader();

        loader.load(url, (object) => {
            // Apply default material to meshes missing one
            object.traverse((child) => {
                if (child.isMesh) {
                    if (!child.material || (Array.isArray(child.material) && child.material.length === 0)) {
                        child.material = new THREE.MeshStandardMaterial({ color: 0x999999, roughness: 0.6, metalness: 0.1 });
                    }
                }
            });

            // Center and fit model
            const box = new THREE.Box3().setFromObject(object);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z);

            object.position.sub(center);
            camera.position.set(maxDim * 0.8, maxDim * 0.6, maxDim * 1.5);
            camera.lookAt(0, 0, 0);

            if (controls) {
                controls.target.set(0, 0, 0);
                controls.update();
            }

            scene.add(object);
        });

        // Animation loop
        let animId;
        const animate = () => {
            animId = requestAnimationFrame(animate);
            if (controls) {
                controls.update();
            } else if (autoRotate) {
                scene.rotation.y += 0.005;
            }
            renderer.render(scene, camera);
        };
        animate();

        return () => {
            cancelAnimationFrame(animId);
            if (controls) controls.dispose();
            renderer.dispose();
            scene.traverse((obj) => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) {
                    if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
                    else obj.material.dispose();
                }
            });
            if (container.contains(renderer.domElement)) {
                container.removeChild(renderer.domElement);
            }
        };
    }, [url, width, height, interactive, autoRotate]);

    return <div ref={mountRef} style={{ width, height, borderRadius: 6, overflow: 'hidden' }} />;
};

export default ThreeModelViewer;
